/**
 * The photos one medication was read from, full screen.
 *
 * Lifted out of DraftCard so a SAVED medication opens exactly the same viewer.
 * Nothing about looking at a label is specific to the draft stage — the nurse
 * checking a strength wants the same pinch, twist and focus magnifier after she
 * has saved the row as before — and two copies of this would drift apart the
 * first time either side was touched.
 *
 * Purely presentational: each card owns its own fetch and passes the result in,
 * because the drafts and the saved list reach the photos through different
 * endpoints. What the viewer DOES own is the state that only means anything
 * while it is open — which photo is showing and whether the magnifier is
 * engaged — so that closing and reopening always starts from the same place.
 */
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useEffect, useState } from "react";
import {
    ActivityIndicator,
    Image,
    Modal,
    Pressable,
    StyleSheet,
    useWindowDimensions,
    View
} from "react-native";
import { Text } from "react-native-paper";

import { ZoomableImage } from "./ZoomableImage";

import type { DraftSourceImage } from "@/lib/api";
import { logRocketRedactProps } from "@/lib/logrocket";
import { colors, fonts, spacing } from "@/lib/theme";

interface Props {
    visible: boolean;
    onClose: () => void;
    /** The medication these photos belong to — shown in the header. */
    title: string;
    images: DraftSourceImage[];
    loading: boolean;
    error: string | null;
}

export function SourcePhotoViewer({ visible, onClose, title, images, loading, error }: Props) {
    const [activeImage, setActiveImage] = useState(0);
    // Focus starts ON where it is offered at all: the nurse opened this to look
    // at one medication, and the whole bench is one tap away.
    const [focusOn, setFocusOn] = useState(true);

    // Reopening starts clean. Without this the viewer remembers the photo and
    // magnifier state of whichever medication was opened last, which reads as a
    // bug the first time two cards on one photo are compared.
    useEffect(() => {
        if (visible) {
            setActiveImage(0);
            setFocusOn(true);
        }
    }, [visible]);

    const window = useWindowDimensions();
    // The photo gets everything the chrome does not: header, thumbnail film and
    // the safe area at the bottom.
    const viewerSize = {
        width: window.width,
        height: Math.max(240, window.height - 168)
    };
    const activeSourceImage = images[activeImage] ?? null;
    const activeFocusBox = activeSourceImage?.box ?? null;
    // Offered only where the photo holds more than one medication AND we know
    // where this one is. On a photo of a single bottle there is nothing to pick
    // out, and a control that visibly does nothing is worse than no control.
    const focusAvailable = Boolean(activeFocusBox && (activeSourceImage?.medicationCount ?? 1) > 1);
    const focusEngaged = focusAvailable && focusOn;

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <View style={ss.viewerRoot}>
                <View style={ss.viewerHeader}>
                    <View style={ss.viewerTitleBlock}>
                        <Text style={ss.viewerTitle} numberOfLines={1}>
                            {title}
                        </Text>
                        <Text style={ss.viewerSubtitle}>
                            {focusEngaged
                                ? "Focused on this medication · the rest of the photo is blurred"
                                : images.length > 1
                                  ? `Photo ${activeImage + 1} of ${images.length} — the label runs across them`
                                  : "Pinch to zoom · twist to rotate · drag to pan"}
                        </Text>
                    </View>
                    <Pressable
                        onPress={onClose}
                        hitSlop={12}
                        accessibilityRole="button"
                        accessibilityLabel="Close photos"
                        style={ss.viewerClose}
                    >
                        <MaterialCommunityIcons name="close" size={24} color="#FFFFFF" />
                    </Pressable>
                </View>

                {loading && (
                    <View style={ss.viewerStatus}>
                        <ActivityIndicator size="large" color="#FFFFFF" />
                    </View>
                )}
                {!loading && error && (
                    <View style={ss.viewerStatus}>
                        <Text style={ss.viewerErrorText}>{error}</Text>
                    </View>
                )}
                {!loading && !error && images[activeImage] && (
                    <ZoomableImage
                        uri={images[activeImage].url}
                        width={viewerSize.width}
                        height={viewerSize.height}
                        accessibilityLabel={`Photo ${images[activeImage].index} of ${title}`}
                        focusBox={activeFocusBox}
                        focusActive={focusEngaged}
                    />
                )}

                {/* Only where this photo shows more than one medication —
                    the case the box was extracted for. */}
                {!loading && !error && focusAvailable && (
                    <View
                        // Floated over the photo rather than laid out below it:
                        // the viewport height is computed once from the window,
                        // and a control that appears and disappears with the
                        // active photo would resize the picture under the
                        // nurse's fingers.
                        style={[ss.viewerFocusBar, { bottom: images.length > 1 ? 96 : 28 }]}
                        pointerEvents="box-none"
                    >
                        <Pressable
                            onPress={() => setFocusOn((on) => !on)}
                            style={[ss.viewerFocusToggle, focusOn && ss.viewerFocusToggleOn]}
                            accessibilityRole="switch"
                            accessibilityState={{ checked: focusOn }}
                            accessibilityLabel={
                                focusOn
                                    ? `Focus on ${title} is on. Tap to show the whole photo.`
                                    : `Focus on ${title} is off. Tap to zoom to it.`
                            }
                        >
                            {/* One icon, not two: the magnifier IS the control,
                                and swapping it for a zoom-out glyph would make
                                the button describe its action while the label
                                describes its state. Fill, pip and label carry
                                on/off. */}
                            <MaterialCommunityIcons
                                name="magnify"
                                size={17}
                                color={focusOn ? "#0B1117" : "#FFFFFF"}
                            />
                            <Text
                                style={[ss.viewerFocusText, focusOn && ss.viewerFocusTextOn]}
                                numberOfLines={1}
                            >
                                {focusOn ? "Focused on this one" : "Find this one"}
                            </Text>
                            <View style={[ss.viewerFocusDot, focusOn && ss.viewerFocusDotOn]} />
                        </Pressable>
                    </View>
                )}

                {/* Only shown for a wrapped label shot in halves, which is the
                    case the nurse most needs to flip between. */}
                {images.length > 1 && (
                    <View style={ss.viewerFilm}>
                        {images.map((image, index) => (
                            <Pressable
                                key={image.index}
                                onPress={() => setActiveImage(index)}
                                style={[
                                    ss.viewerThumb,
                                    index === activeImage && ss.viewerThumbActive
                                ]}
                                accessibilityRole="button"
                                accessibilityLabel={`Show photo ${image.index}`}
                            >
                                <Image
                                    {...logRocketRedactProps()}
                                    source={{ uri: image.url }}
                                    style={ss.viewerThumbImage}
                                    resizeMode="cover"
                                />
                            </Pressable>
                        ))}
                    </View>
                )}
            </View>
        </Modal>
    );
}

const ss = StyleSheet.create({
    viewerRoot: {
        flex: 1,
        backgroundColor: "#0B1117"
    },
    viewerHeader: {
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        paddingTop: 56,
        paddingHorizontal: spacing.lg,
        paddingBottom: 14
    },
    viewerTitleBlock: {
        flex: 1
    },
    viewerTitle: {
        fontSize: 16,
        fontFamily: fonts.extraBold,
        color: "#FFFFFF"
    },
    viewerSubtitle: {
        marginTop: 3,
        fontSize: 12,
        fontFamily: fonts.medium,
        color: "rgba(255,255,255,0.66)"
    },
    viewerClose: {
        width: 34,
        height: 34,
        borderRadius: 17,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255,255,255,0.12)"
    },
    viewerStatus: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: spacing.xl
    },
    viewerErrorText: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: "rgba(255,255,255,0.8)",
        textAlign: "center"
    },
    viewerFocusBar: {
        position: "absolute",
        left: 0,
        right: 0,
        alignItems: "center"
    },
    viewerFocusToggle: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingLeft: 14,
        paddingRight: 12,
        paddingVertical: 9,
        borderRadius: 999,
        borderWidth: 1.5,
        borderColor: "rgba(255,255,255,0.22)",
        backgroundColor: "rgba(255,255,255,0.10)"
    },
    viewerFocusToggleOn: {
        borderColor: colors.teal400,
        backgroundColor: colors.teal400
    },
    viewerFocusText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    viewerFocusTextOn: {
        color: "#0B1117"
    },
    // A lit pip, so on/off reads at a glance and not only from the fill colour.
    viewerFocusDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: "rgba(255,255,255,0.35)"
    },
    viewerFocusDotOn: {
        backgroundColor: "#0B1117"
    },
    viewerFilm: {
        flexDirection: "row",
        gap: 10,
        paddingVertical: 14,
        paddingHorizontal: spacing.lg
    },
    viewerThumb: {
        width: 52,
        height: 52,
        borderRadius: 8,
        overflow: "hidden",
        borderWidth: 2,
        borderColor: "transparent",
        opacity: 0.55
    },
    viewerThumbActive: {
        borderColor: colors.teal400,
        opacity: 1
    },
    viewerThumbImage: {
        width: "100%",
        height: "100%"
    }
});
